import { useCallback, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { AttachKind } from './types';

export interface AttachSummary {
  succeeded: string[];
  failed: { id: string; message: string }[];
}

export interface UseAttachMultipleArgs {
  kind: AttachKind;
  activeOrgId: string | null;
  projectId: string | undefined;
}

export interface UseAttachMultiple {
  attach: (ids: string[]) => Promise<AttachSummary>;
  attaching: boolean;
  lastSummary: AttachSummary | null;
  reset: () => void;
}

interface AttachOneResult {
  ok: boolean;
  message?: string;
}

/** Idempotent batch attach for the attach-existing modal. Attaches items one at
 *  a time so a single failure doesn't abort the rest; returns a per-id summary. */
export function useAttachMultiple(args: UseAttachMultipleArgs): UseAttachMultiple {
  const { kind, activeOrgId, projectId } = args;
  const [attaching, setAttaching] = useState(false);
  const [lastSummary, setLastSummary] = useState<AttachSummary | null>(null);

  const attachOne = useCallback(
    async (id: string): Promise<AttachOneResult> => {
      if (!activeOrgId || !projectId) {
        return { ok: false, message: 'No active org/project' };
      }
      if (kind === 'documents') {
        const { error } = await supabase
          .from('project_documents')
          .upsert(
            { org_id: activeOrgId, project_id: projectId, document_id: id },
            { onConflict: 'project_id,document_id', ignoreDuplicates: true }
          );
        return error ? { ok: false, message: error.message } : { ok: true };
      }
      const { error } = await supabase.rpc('set_work_item_project', {
        p_work_item_id: id,
        p_project_id: projectId
      });
      return error ? { ok: false, message: error.message } : { ok: true };
    },
    [kind, activeOrgId, projectId]
  );

  const attach = useCallback(
    async (ids: string[]): Promise<AttachSummary> => {
      setAttaching(true);
      const summary: AttachSummary = { succeeded: [], failed: [] };
      for (const id of ids) {
        const res = await attachOne(id);
        if (res.ok) {
          summary.succeeded.push(id);
        } else {
          summary.failed.push({ id, message: res.message ?? 'Failed to attach' });
        }
      }
      setLastSummary(summary);
      setAttaching(false);
      return summary;
    },
    [attachOne]
  );

  const reset = useCallback(() => setLastSummary(null), []);

  return { attach, attaching, lastSummary, reset };
}
